'use client';

import { ownedImageProps } from '@lib/asset-renditions';
import { styled } from 'styled-components';
import Button from './Button';
import Image from '@dvd/components/asset-image';
import { Icon } from '@wistia/ui';
import TextLink from './TextLink';
import Tag, { CategoryTags, FeatureTags, PricingTags } from './Tag';
import { CtaProps, ImageProps } from '@types';

export type ProfileCardProps = {
  /**
   * The name of the integration.
   */
  name: string;
  /**
   * The number of installs of the integration. This should be a number -- no commas.
   */
  numberOfInstalls?: number;
  /**
   * The integration logo displayed at the top of card.
   */
  image: ImageProps;
  /**
   * Whether the integration is published by Wistia.
   */
  publishedByWistia?: boolean;
  /**
   * @deprecated No longer used on integration pages. Will be removed or redesigned in the near future.
   */
  availablePlans?: PricingTags[];
  /**
   * The works with tags of the integration.
   */
  worksWith?: (CategoryTags | FeatureTags)[];
  /**
   * The resources of the integration.
   */
  resources?: CtaProps[];
  /**
   * Cta for the card; required.
   */
  cta: CtaProps;
};

// This is a helper function to format the number of installs in a compact format.
// For example, 1000 becomes 1k, 1000000 becomes 1M, etc.
const formatNumber = (num: number) => {
  // For thousands (short notation)
  if (num >= 1000 && num < 1000000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  }

  // For millions and billions (short notation)
  const formatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
  });
  return formatter.format(num);
};

const PublishDetails = styled.span`
  color: ${({ theme }) => theme.colors.grey500};
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.typography.fontSize.bodyXs};
  gap: ${({ theme }) => theme.spacing[0.25]};
  margin-block-start: ${({ theme }) => theme.spacing[0.5]};
  flex-wrap: wrap;
`;

const ProfileCardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  grid-column: 1;
  grid-row: 4;

  ${({ theme }) => theme.mq.minWidth.medium} {
    justify-content: start;
    grid-column: 2;
    grid-row: 2 / span 3;
  }
  ${({ theme }) => theme.mq.minWidth.large} {
    justify-content: center;
    grid-column: 1;
    grid-row: 4;
  }
`;

const ProfileCardImage = styled(Image)`
  border: 1px solid ${({ theme }) => theme.colors.grey200};
  border-radius: ${({ theme }) => theme.radius.std};
  grid-column: 1;
  grid-row: 1;
  height: auto;
  width: 100%;

  ${({ theme }) => theme.mq.minWidth.medium} {
    grid-column: 1;
    grid-row: 1 / span 2;
    place-self: end;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    grid-column: 1;
    grid-row: 1;
  }
`;

const ProfileNameAndPublishDetails = styled.div`
  grid-column: 1;
  grid-row: 2;

  ${({ theme }) => theme.mq.minWidth.medium} {
    grid-column: 2;
    grid-row: 1;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    grid-column: 1;
    grid-row: 2;
  }
`;

const ProfileCardButton = styled(Button)`
  margin-block-start: ${({ theme }) => theme.spacing[1]};
  margin-block-end: 0;
  display: block;
  width: 100%;
  text-align: center;
  grid-column: 1;
  grid-row: 3;

  ${({ theme }) => theme.mq.minWidth.medium} {
    grid-column: 1;
    grid-row: 3 / span 2;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    display: block;
    margin-block-start: ${({ theme }) => theme.spacing[1.25]};
    grid-column: 1;
    grid-row: 3;
  }
`;

/* The name is the page's h1, styled as an h2 so it sits inside the card. */
const ProfileCardName = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize.headingTwo};
  font-weight: ${({ theme }) => theme.typography.fontWeight.headingTwo};
  letter-spacing: normal;
  line-height: ${({ theme }) => theme.typography.lineHeight.headingTwo};
  margin-block-start: ${({ theme }) => theme.spacing[0.75]};
  margin-block-end: 0;

  ${({ theme }) => theme.mq.minWidth.medium} {
    margin-block-start: 0;
  }
  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${({ theme }) => theme.typography.fontSize.desktop.headingTwo};
    margin-block-start: ${({ theme }) => theme.spacing[0.75]};
  }
`;

const ProfileCardTagList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[0.75]};
  padding: 0;
  margin: 0;
  list-style: none;
  text-align: start;
  align-items: flex-start;
  justify-content: flex-start;
  max-width: 100%;
`;

const ProfileCardList = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
  text-align: start;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-wrap: wrap;
  row-gap: ${({ theme }) => theme.spacing[0.5]};
`;

const ProfileCardListItem = styled.li`
  font-size: ${({ theme }) => theme.typography.fontSize.bodySm};

  ${({ theme }) => theme.mq.minWidth.medium} {
    font-size: ${({ theme }) => theme.typography.fontSize.desktop.bodySm};
  }
`;

const ProfileCardWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.radius.std};
  box-shadow: ${({ theme }) => theme.elevation.elevation2()};
  padding: ${({ theme }) => theme.spacing[1.5]};
  display: grid;
  align-items: center;
  justify-content: center;
  gap: 0;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(4, auto);

  ${({ theme }) => theme.mq.minWidth.small} {
    width: 100%;
  }

  ${({ theme }) => theme.mq.minWidth.medium} {
    grid-template-columns: 1fr 1fr;
    align-items: flex-start;
    justify-content: flex-start;
    column-gap: ${({ theme }) => theme.spacing[2]};
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    grid-template-columns: 1fr;
    align-items: center;
    justify-content: center;
    gap: 0;
  }
`;

const ProfileCardHeading = styled.h5`
  margin-block-end: ${({ theme }) => theme.spacing[0.5]};
  margin-block-start: ${({ theme }) => theme.spacing[1]};

  ${({ theme }) => theme.mq.minWidth.large} {
    margin-block-start: ${({ theme }) => theme.spacing[0.75]};
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    margin-block-start: ${({ theme }) => theme.spacing[1.25]};
  }
`;

const renderPublishDetails = (
  publishedByWistia: boolean,
  numberOfInstalls: number,
) => {
  return (
    <PublishDetails>
      {publishedByWistia && (
        <>
          <Icon type="checkmark-circle" size="sm" /> Published by Wistia
        </>
      )}
      {publishedByWistia && numberOfInstalls > 0 && ' | '}
      {numberOfInstalls > 0 && (
        <>
          <Icon type="download" size="sm" />{' '}
          {formatNumber(numberOfInstalls).toString()} installs
        </>
      )}
    </PublishDetails>
  );
};

/**
 *
 * Profile Card: A way to display integration info at a glance
 */
const ProfileCard = ({
  name,
  numberOfInstalls = null,
  image,
  publishedByWistia = false,
  worksWith = null,
  resources = null,
  availablePlans = null,
  cta,
}: ProfileCardProps) => {
  return (
    <ProfileCardWrapper>
      <ProfileCardImage
        sizes="100vw"
        src={image.src}
        {...ownedImageProps(image.src)}
        alt={image.alt}
        width={image.width}
        height={image.height}
      />

      <ProfileNameAndPublishDetails>
        <ProfileCardName>{name}</ProfileCardName>
        {renderPublishDetails(publishedByWistia, numberOfInstalls)}
      </ProfileNameAndPublishDetails>

      <ProfileCardButton
        href={cta.href}
        variant="primary"
        trackingLabel={cta.trackingLabel}
        trackingProperty={cta.trackingProperty}
        size="mini"
      >
        {cta.text}
      </ProfileCardButton>
      <ProfileCardContent>
        {worksWith && (
          <>
            <ProfileCardHeading>Works with</ProfileCardHeading>
            <ProfileCardTagList>
              {worksWith.map((item) => (
                <ProfileCardListItem key={item}>
                  <Tag tag={item} />
                </ProfileCardListItem>
              ))}
            </ProfileCardTagList>
          </>
        )}
        {resources && (
          <>
            <ProfileCardHeading>Resources</ProfileCardHeading>
            <ProfileCardList>
              {resources.map((item) => (
                <ProfileCardListItem key={item.text}>
                  <TextLink cta={item} />
                </ProfileCardListItem>
              ))}
            </ProfileCardList>
          </>
        )}
        {availablePlans && (
          <>
            <ProfileCardHeading>Available Plans</ProfileCardHeading>
            <ProfileCardTagList>
              {availablePlans.map((plan) => (
                <ProfileCardListItem key={plan}>
                  <Tag tag={plan} />
                </ProfileCardListItem>
              ))}
            </ProfileCardTagList>
          </>
        )}
      </ProfileCardContent>
    </ProfileCardWrapper>
  );
};

export default ProfileCard;
